import { useEffect, useState } from 'react';
import {
  Badge,
  Balance,
  Button,
  Callout,
  KeyValue,
  Modal,
  Money,
  SkeletonRows,
  cn,
  formatDateTime,
  formatDate,
  formatMoney,
} from '@biovet/ui';
import { ApiError, apiGet, type VoucherDetail } from '../lib/api.js';
import { printReport, type ReportTable } from '../lib/print.js';

/**
 * One voucher, in full, over whatever screen asked for it.
 *
 * The general ledger shows a voucher number against a single account — one side
 * of an entry, with no way to see what the other side was without leaving the
 * screen and finding the voucher. That is the wrong shape for the question the
 * number actually raises, which is always "what was this entry?". So the number
 * opens the whole document: every line, both sides, the totals that had to
 * agree, and the audit trail behind them.
 *
 * **The audit trail is not decoration here.** The ledger is append-only — a
 * posted voucher cannot be edited, only reversed — so who created it, who
 * submitted it, who approved it and who posted it is the entire account of how
 * the figures came to be in the book. Each stamp renders only when it happened;
 * a draft that has never been posted says so by having no posted row, rather
 * than by showing an empty one.
 *
 * It fetches on open rather than being handed a voucher, because the caller has
 * a number and a type and nothing else — and the detail is far larger than any
 * ledger row, so loading it for every row up front would be the wrong trade.
 */

export function VoucherDetailModal({
  voucherType,
  voucherNo,
  onClose,
  onOpenLedger,
}: {
  /**
   * Any value of the `voucher_type` enum — the five a person types, and the
   * ones an operational document raises: PUR, INV, SRN, PRN, COM, CRUN, GRN,
   * STR, ADJ, OPN, REV, DEP, CLO. A ledger row carries whichever posted it, and
   * the number against it raises the same question either way. Null closes the
   * dialog.
   */
  voucherType: string | null;
  voucherNo: string | null;
  onClose: () => void;
  /** Clicking a line's account jumps the ledger to it. */
  onOpenLedger?: (accountCode: string) => void;
}) {
  const [detail, setDetail] = useState<VoucherDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const open = Boolean(voucherType && voucherNo);

  useEffect(() => {
    if (!voucherType || !voucherNo) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // Cleared so a slow second request never shows the previous voucher's lines
    // under the new voucher's number.
    setDetail(null);

    apiGet<VoucherDetail>(
      `/vouchers/${encodeURIComponent(voucherType)}/${encodeURIComponent(voucherNo)}`,
      {},
      controller.signal,
    )
      .then((result) => {
        setDetail(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          cause instanceof ApiError
            ? cause.full
            : 'Cannot reach the API. Start it with `pnpm api`.',
        );
        setLoading(false);
      });

    return () => controller.abort();
  }, [voucherType, voucherNo]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <span className="bb-mono">{voucherNo}</span>
          {detail && <Badge status={detail.status} />}
          {detail?.reversedByNo && <Badge tone="bad">reversed</Badge>}
        </span>
      }
      subtitle={
        detail
          ? `${formatDate(detail.voucherDate)} · ${detail.periodName ?? 'no period'} · ${
              detail.branchName ?? detail.branchCode ?? 'no branch'
            }`
          : voucherType
            ? `${voucherType} voucher`
            : undefined
      }
      /*
        The voucher, not the page it is floating above.

        `window.print()` here printed the modal *and* the general ledger behind
        it — the dialog is an overlay, not a document, so the paper came out with
        four hundred ledger rows underneath the entry somebody wanted. This
        composes the entry: both sides, the party and narration on each line, and
        the two totals that had to agree.
      */
      actions={
        detail && (
          <Button
            variant="secondary"
            size="sm"
            data-noprint="1"
            onClick={() => printReport(voucherReport(detail))}
          >
            Print
          </Button>
        )
      }
      footer={
        detail && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[11.5px] text-[var(--ink-3)]">
              {detail.lineCount} line{detail.lineCount === 1 ? '' : 's'}
              {detail.currency !== 'PKR' && ` · ${detail.currency} at ${detail.fxRate}`}
            </span>
            <span className="flex items-center gap-3 text-[12px]">
              <span className="text-[var(--ink-3)]">Total</span>
              <Money value={detail.totalDebitBase} className="text-[12.5px]" />
              <span className="text-[var(--ink-3)]">Dr</span>
              <Money value={detail.totalCreditBase} className="text-[12.5px]" />
              <span className="text-[var(--ink-3)]">Cr</span>
            </span>
          </div>
        )
      }
    >
      {error && (
        <div className="p-[var(--pad)]">
          <Callout tone="danger" title="Could not load this voucher">
            {error}
          </Callout>
        </div>
      )}

      {loading && (
        <div className="p-[var(--pad)]">
          <SkeletonRows rows={6} cols={4} />
        </div>
      )}

      {detail && (
        <>
          {detail.narration && (
            <p className="m-0 border-b border-[var(--line-2)] px-[var(--pad)] py-2.5 text-[12.5px] text-[var(--ink-2)]">
              {detail.narration}
            </p>
          )}

          {(detail.reversedByNo || detail.reversalOfNo) && (
            <div className="px-[var(--pad)] pt-3">
              <Callout
                tone={detail.reversedByNo ? 'warn' : 'info'}
                title={
                  detail.reversedByNo ? 'This voucher has been reversed' : 'This is a reversal'
                }
              >
                {detail.reversedByNo
                  ? `${detail.reversedByNo} cancels it out. The lines below stay in the ledger — it is append-only, so a correction is a new entry rather than an edit.`
                  : `It reverses ${detail.reversalOfNo}.`}
                {detail.reversalReason ? ` Reason: ${detail.reversalReason}` : ''}
              </Callout>
            </div>
          )}

          <table className="w-full border-collapse" aria-label={`Lines of ${detail.voucherNo}`}>
            <thead>
              <tr className="bg-[var(--surface-2)]">
                <Th width="34px">#</Th>
                <Th>Account</Th>
                <Th>Particulars</Th>
                <Th width="140px" align="right">
                  Debit
                </Th>
                <Th width="140px" align="right">
                  Credit
                </Th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => (
                <tr key={line.lineNo} className="border-b border-[var(--line-2)] align-top">
                  <td className="bb-mono px-3 py-2 text-[11.5px] text-[var(--ink-3)]">
                    {line.lineNo}
                  </td>
                  <td className="px-2 py-2">
                    {/*
                      The head above the account, when there is one. The ledger
                      stores only the leaf, so `1131-0004 Rahim Traders` alone
                      does not say it is a receivable — the parent is what makes
                      the line legible.
                    */}
                    {line.parentCode && (
                      <span className="block text-[11px] text-[var(--ink-3)]">
                        {line.parentCode} {line.parentName}
                      </span>
                    )}
                    {onOpenLedger ? (
                      <button
                        type="button"
                        onClick={() => onOpenLedger(line.accountCode)}
                        className={cn(
                          'cursor-pointer border-0 bg-transparent p-0 text-left',
                          'text-[12.5px] text-[var(--accent)] hover:underline',
                        )}
                      >
                        <span className="bb-mono">{line.accountCode}</span> {line.accountName}
                      </button>
                    ) : (
                      <span className="text-[12.5px] text-[var(--ink)]">
                        <span className="bb-mono">{line.accountCode}</span> {line.accountName}
                      </span>
                    )}
                    {line.partyName && (
                      <span className="block text-[11px] text-[var(--ink-3)]">
                        {line.partyName}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[12px] text-[var(--ink-2)]">
                    {line.narration ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={line.debitBase} dashOnZero />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Money value={line.creditBase} dashOnZero />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[var(--surface-2)] font-medium">
              <tr>
                <td />
                <td className="px-2 py-2 text-[12px] text-[var(--ink-2)]" colSpan={2}>
                  Totals — equal by <code className="bb-mono">trg_vouchers_balance</code>
                </td>
                <td className="px-3 py-2 text-right">
                  <Money value={detail.totalDebitBase} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Money value={detail.totalCreditBase} />
                </td>
              </tr>
            </tfoot>
          </table>

          <div className="border-t border-[var(--line)] px-[var(--pad)] py-3">
            <h3 className="m-0 pb-2 text-[11px] font-medium tracking-[0.08em] text-[var(--ink-3)] uppercase">
              Audit trail
            </h3>
            <KeyValue items={auditItems(detail)} />
          </div>
        </>
      )}
    </Modal>
  );
}

/**
 * The stamps that actually happened, in the order they happen.
 *
 * A row is omitted rather than shown empty: "Approved by —" invites the reader
 * to wonder whether approval was skipped or merely unrecorded, and on an
 * append-only ledger that is not a distinction to leave ambiguous. `Edited` is
 * suppressed when it matches creation, because every row is written once and an
 * `updated_at` equal to `created_at` records no edit at all.
 */
function auditItems(detail: VoucherDetail): Array<{ label: string; value: string }> {
  const items: Array<{ label: string; value: string }> = [];

  const stamp = (label: string, at: string | null, by: string | null) => {
    if (!at) return;
    items.push({ label, value: by ? `${by} · ${formatDateTime(at)}` : formatDateTime(at) });
  };

  stamp('Created', detail.createdAt, detail.createdByName);
  stamp('Submitted', detail.submittedAt, detail.submittedByName);
  stamp('Approved', detail.approvedAt, detail.approvedByName);
  stamp('Posted', detail.postedAt, detail.postedByName);
  if (detail.updatedAt && detail.updatedAt !== detail.createdAt) {
    stamp('Last edited', detail.updatedAt, detail.updatedByName);
  }

  items.push({ label: 'Status', value: detail.status.toLowerCase().replace(/_/g, ' ') });
  items.push({
    label: 'Currency',
    value: detail.currency === 'PKR' ? 'PKR' : `${detail.currency} at ${detail.fxRate}`,
  });
  if (detail.partyName) items.push({ label: 'Header party', value: detail.partyName });

  return items;
}

function Th({
  children,
  width,
  align = 'left',
}: {
  children: React.ReactNode;
  width?: string;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={cn(
        'border-b border-[var(--line-2)] px-3 py-2',
        'text-[10px] font-medium tracking-[0.08em] whitespace-nowrap text-[var(--ink-3)] uppercase',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

/**
 * The voucher as a printed document.
 *
 * `printVoucher()` in `VoucherEntry.tsx` stays where it is: it prints the
 * voucher an accountant is *entering*, so it knows the type's own configuration,
 * the draft on screen and the two signature lines a fresh document needs. This
 * one prints a voucher already in the book, from what `GET /api/vouchers/:type/:no`
 * answers — which is every type in the enum, not the five a person types.
 *
 * Both sides on one table with a total row, because a voucher's whole claim is
 * that the two columns agree, and a printed copy where that cannot be checked is
 * not a copy of a voucher.
 */
function voucherReport(detail: VoucherDetail): ReportTable {
  return {
    title: `${detail.voucherType} — ${detail.voucherNo}`,
    fileName: detail.voucherNo,
    meta: [
      formatDate(detail.voucherDate),
      detail.periodName,
      // The branch sits here rather than in the letterhead. It is a fact about
      // *this voucher* — which of several sets of books it posted into — and
      // under the company name it read as part of the company's address.
      detail.branchName,
      detail.status,
      detail.currency === 'PKR' ? 'PKR' : `${detail.currency} at ${detail.fxRate}`,
      detail.partyName,
      detail.reversedByNo ? `reversed by ${detail.reversedByNo}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    columns: [
      { label: '#', width: '32px' },
      { label: 'Account', width: '90px' },
      { label: 'Particulars' },
      { label: 'Debit', align: 'right', width: '116px' },
      { label: 'Credit', align: 'right', width: '116px' },
    ],
    rows: [
      ...detail.lines.map((line) => [
        String(line.lineNo),
        line.accountCode,
        [line.accountName, line.partyName, line.narration].filter(Boolean).join(' · '),
        line.debitBase === '0' ? '' : formatMoney(line.debitBase),
        line.creditBase === '0' ? '' : formatMoney(line.creditBase),
      ]),
      {
        cells: [
          '',
          '',
          'Total',
          formatMoney(detail.totalDebitBase),
          formatMoney(detail.totalCreditBase),
        ],
        total: true,
      },
    ],
    notes: [
      detail.narration ? `Narration: ${detail.narration}` : '',
      detail.postedAt && detail.postedByName
        ? `Posted ${formatDateTime(detail.postedAt)} by ${detail.postedByName}.`
        : '',
      detail.reversalReason ? `Reversal reason: ${detail.reversalReason}` : '',
    ].filter(Boolean),
  };
}
